/**
 * services/adminApi.ts
 * 
 * Admin-specific API service functions for the admin dashboard.
 * Provides centralized access to admin functionality with proper
 * error handling and type safety.
 */

import { 
  AdminDashboardStats, 
  AdminUserManagement, 
  AdminChatbotManagement,
  AdminAnalytics,
  AdminFinancials,
  AdminSystemLog,
  AdminAuditLog,
  AdminNotification,
  AdminSystemSettings,
  AdminFilters,
  AdminSort,
  AdminBulkAction
} from '../types/admin'

// Mock delay for realistic API simulation
const mockDelay = (ms: number) => new Promise(resolve => setTimeout(resolve, ms))

// Admin Authentication
export const adminAuthApi = {
  login: async (email: string, password: string) => {
    await mockDelay(1000)
    
    // Special admin credentials
    if (email === 'admin@chatfolio.com' && password === 'admin123') {
      return {
        success: true,
        data: {
          user: {
            id: 'admin-1',
            email: 'admin@chatfolio.com',
            name: 'Admin User',
            role: 'admin',
            permissions: [
              'view_users', 'edit_users', 'delete_users',
              'view_chatbots', 'edit_chatbots', 'delete_chatbots',
              'view_analytics', 'view_financials', 'manage_system', 'view_logs'
            ],
            lastLogin: new Date().toISOString(),
            createdAt: '2024-01-01T00:00:00Z',
            updatedAt: new Date().toISOString()
          },
          token: 'admin-token-' + Math.random().toString(36).substr(2, 9)
        }
      }
    }
    
    return {
      success: false,
      error: 'Invalid admin credentials'
    }
  },

  logout: async () => {
    await mockDelay(500)
    return { success: true }
  },

  validateToken: async (token: string) => {
    await mockDelay(300)
    return { success: true, data: { isValid: true } }
  }
}

// Admin Dashboard Data
export const adminDashboardApi = {
  getStats: async (): Promise<AdminDashboardStats> => {
    await mockDelay(800)
    return {
      totalUsers: 1247,
      totalChatbots: 89,
      totalRevenue: 45678.90,
      totalMessages: 156789,
      activeUsers: 892,
      newUsersThisMonth: 156,
      newChatbotsThisMonth: 23,
      revenueThisMonth: 12345.67
    }
  },

  getAnalytics: async (): Promise<AdminAnalytics> => {
    await mockDelay(1000)
    return {
      userGrowth: Array.from({ length: 30 }, (_, i) => ({
        date: new Date(Date.now() - (29 - i) * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
        newUsers: Math.floor(Math.random() * 20) + 5,
        activeUsers: Math.floor(Math.random() * 100) + 50
      })),
      revenueGrowth: Array.from({ length: 30 }, (_, i) => ({
        date: new Date(Date.now() - (29 - i) * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
        revenue: Math.floor(Math.random() * 500) + 100,
        transactions: Math.floor(Math.random() * 50) + 10
      })),
      chatbotUsage: [
        { chatbotId: '1', chatbotName: 'AI Assistant', messages: 12345, users: 234, revenue: 1234.56 },
        { chatbotId: '2', chatbotName: 'Customer Support', messages: 9876, users: 156, revenue: 987.65 },
        { chatbotId: '3', chatbotName: 'Code Helper', messages: 7654, users: 89, revenue: 654.32 },
        { chatbotId: '4', chatbotName: 'Language Tutor', messages: 5432, users: 67, revenue: 432.10 },
        { chatbotId: '5', chatbotName: 'Health Advisor', messages: 4321, users: 45, revenue: 321.09 }
      ],
      systemHealth: {
        cpu: 45.2,
        memory: 67.8,
        disk: 23.4,
        activeConnections: 156
      }
    }
  },

  getFinancials: async (period: string = '30d'): Promise<AdminFinancials> => {
    await mockDelay(600)
    return {
      totalRevenue: 45678.90,
      monthlyRevenue: 12345.67,
      pendingPayouts: 5678.90,
      commissionEarned: 3456.78,
      revenueBreakdown: {
        subscriptions: 23456.78,
        credits: 12345.67,
        oneTime: 9876.45
      },
      topEarners: [
        { userId: 'user-1', userName: 'John Creator', revenue: 2345.67, chatbots: 5 },
        { userId: 'user-2', userName: 'Sarah Developer', revenue: 1876.54, chatbots: 3 },
        { userId: 'user-3', userName: 'Mike Designer', revenue: 1543.21, chatbots: 2 },
        { userId: 'user-4', userName: 'Lisa Writer', revenue: 1234.56, chatbots: 4 },
        { userId: 'user-5', userName: 'Tom Artist', revenue: 987.65, chatbots: 1 }
      ]
    }
  }
}

// User Management
export const adminUserApi = {
  getUsers: async (filters?: AdminFilters, sort?: AdminSort, page = 1, limit = 20) => {
    await mockDelay(800)
    
    const mockUsers: AdminUserManagement[] = Array.from({ length: 50 }, (_, i) => ({
      id: `user-${i + 1}`,
      email: `user${i + 1}@example.com`,
      name: `User ${i + 1}`,
      role: i % 3 === 0 ? 'creator' : 'user',
      status: i % 10 === 0 ? 'suspended' : 'active',
      credits: Math.floor(Math.random() * 1000),
      balance: Math.floor(Math.random() * 500),
      totalChatbots: i % 3 === 0 ? Math.floor(Math.random() * 10) : 0,
      totalMessages: Math.floor(Math.random() * 5000),
      lastActive: new Date(Date.now() - Math.random() * 7 * 24 * 60 * 60 * 1000).toISOString(),
      createdAt: new Date(Date.now() - Math.random() * 365 * 24 * 60 * 60 * 1000).toISOString(),
      isOnline: Math.random() > 0.7
    }))

    return {
      success: true,
      data: {
        users: mockUsers.slice((page - 1) * limit, page * limit),
        total: mockUsers.length,
        page,
        limit,
        totalPages: Math.ceil(mockUsers.length / limit)
      }
    }
  },

  getUser: async (userId: string) => {
    await mockDelay(500)
    return {
      success: true,
      data: {
        id: userId,
        email: `user@example.com`,
        name: 'Test User',
        role: 'user',
        status: 'active',
        credits: 500,
        balance: 100,
        totalChatbots: 2,
        totalMessages: 1500,
        lastActive: new Date().toISOString(),
        createdAt: '2024-01-01T00:00:00Z',
        isOnline: true
      }
    }
  },

  updateUser: async (userId: string, updates: Partial<AdminUserManagement>) => {
    await mockDelay(1000)
    return { success: true, data: { ...updates, id: userId } }
  },

  suspendUser: async (userId: string, reason: string) => {
    await mockDelay(800)
    return { success: true, data: { userId, status: 'suspended', reason } }
  },

  activateUser: async (userId: string) => {
    await mockDelay(800)
    return { success: true, data: { userId, status: 'active' } }
  },

  deleteUser: async (userId: string) => {
    await mockDelay(1000)
    return { success: true, data: { userId } }
  },

  bulkAction: async (action: AdminBulkAction) => {
    await mockDelay(1500)
    return { success: true, data: { action, affectedCount: action.targetIds.length } }
  }
}

// Chatbot Management
export const adminChatbotApi = {
  getChatbots: async (filters?: any) => {
    await mockDelay(800)
    
    const mockChatbots = Array.from({ length: 30 }, (_, i) => ({
      id: `chatbot-${i + 1}`,
      name: `Chatbot ${i + 1}`,
      creator: {
        id: `user-${i + 1}`,
        name: `Creator ${i + 1}`,
        email: `creator${i + 1}@example.com`
      },
      status: i % 5 === 0 ? 'suspended' : i % 7 === 0 ? 'flagged' : i % 9 === 0 ? 'pending' : 'active',
      category: ['Business', 'Education', 'Entertainment', 'Health', 'Technology'][i % 5],
      price: i % 3 === 0 ? 0 : Math.floor(Math.random() * 50) + 5,
      priceType: i % 3 === 0 ? 'free' : i % 2 === 0 ? 'paid' : 'subscription',
      totalUsers: Math.floor(Math.random() * 1000),
      totalMessages: Math.floor(Math.random() * 10000),
      rating: Math.random() * 5,
      revenue: Math.floor(Math.random() * 5000),
      createdAt: new Date(Date.now() - Math.random() * 365 * 24 * 60 * 60 * 1000).toISOString(),
      lastActive: new Date(Date.now() - Math.random() * 7 * 24 * 60 * 60 * 1000).toISOString(),
      description: `Description for chatbot ${i + 1}`,
      tags: ['AI', 'Assistant', 'Helpful']
    }))

    return mockChatbots
  },

  updateChatbot: async (chatbotId: string, updates: Partial<AdminChatbotManagement>) => {
    await mockDelay(1000)
    return { success: true, data: { ...updates, id: chatbotId } }
  },

  bulkAction: async (chatbotIds: string[], action: string) => {
    await mockDelay(1500)
    return { success: true, data: { action, affectedCount: chatbotIds.length } }
  },

  getChatbot: async (chatbotId: string) => {
    await mockDelay(500)
    return {
      success: true,
      data: {
        id: chatbotId,
        name: 'Test Chatbot',
        description: 'Test description',
        creator: {
          id: 'user-1',
          name: 'Test Creator',
          email: 'creator@example.com'
        },
        status: 'active',
        category: 'AI Assistant',
        isFree: false,
        price: 9.99,
        subscribers: 150,
        totalMessages: 2500,
        rating: 4.5,
        createdAt: '2024-01-01T00:00:00Z',
        lastActivity: new Date().toISOString(),
        isPublic: true
      }
    }
  },

  suspendChatbot: async (chatbotId: string, reason: string) => {
    await mockDelay(800)
    return { success: true, data: { chatbotId, status: 'suspended', reason } }
  },

  activateChatbot: async (chatbotId: string) => {
    await mockDelay(800)
    return { success: true, data: { chatbotId, status: 'active' } }
  },

  deleteChatbot: async (chatbotId: string) => {
    await mockDelay(1000)
    return { success: true, data: { chatbotId } }
  }
}

// System Management
export const adminSystemApi = {
  getSystemHealth: async () => {
    await mockDelay(600)
    return {
      status: Math.random() > 0.8 ? 'warning' : Math.random() > 0.95 ? 'critical' : 'healthy',
      uptime: '15 days, 7 hours, 32 minutes',
      cpuUsage: Math.floor(Math.random() * 30) + 20,
      memoryUsage: Math.floor(Math.random() * 40) + 40,
      diskUsage: Math.floor(Math.random() * 20) + 15,
      activeConnections: Math.floor(Math.random() * 200) + 100,
      lastCheck: new Date().toISOString()
    }
  },

  getSystemConfigs: async () => {
    await mockDelay(500)
    return [
      {
        id: '1',
        name: 'Platform Name',
        value: 'Chatfolio',
        description: 'The name of the platform displayed to users',
        category: 'general' as const,
        editable: true
      },
      {
        id: '2',
        name: 'Commission Rate',
        value: '0.15',
        description: 'Percentage of revenue shared with creators',
        category: 'financial' as const,
        editable: true
      },
      {
        id: '3',
        name: 'Max File Size',
        value: '10485760',
        description: 'Maximum file upload size in bytes (10MB)',
        category: 'performance' as const,
        editable: true
      },
      {
        id: '4',
        name: 'Maintenance Mode',
        value: 'false',
        description: 'Enable maintenance mode to restrict access',
        category: 'system' as const,
        editable: true
      },
      {
        id: '5',
        name: 'Registration Enabled',
        value: 'true',
        description: 'Allow new user registrations',
        category: 'security' as const,
        editable: true
      },
      {
        id: '6',
        name: 'Email Verification Required',
        value: 'true',
        description: 'Require email verification for new accounts',
        category: 'security' as const,
        editable: true
      },
      {
        id: '7',
        name: 'Max Chatbots Per User',
        value: '10',
        description: 'Maximum number of chatbots a user can create',
        category: 'performance' as const,
        editable: true
      },
      {
        id: '8',
        name: 'Max Messages Per Day',
        value: '1000',
        description: 'Maximum messages a user can send per day',
        category: 'performance' as const,
        editable: true
      }
    ]
  },

  getRecentLogs: async () => {
    await mockDelay(400)
    return [
      {
        id: '1',
        timestamp: new Date().toISOString(),
        level: 'info' as const,
        component: 'System',
        message: 'System health check completed successfully',
        details: 'All systems operational'
      },
      {
        id: '2',
        timestamp: new Date(Date.now() - 5 * 60 * 1000).toISOString(),
        level: 'warning' as const,
        component: 'Database',
        message: 'High memory usage detected',
        details: 'Memory usage at 85%'
      },
      {
        id: '3',
        timestamp: new Date(Date.now() - 15 * 60 * 1000).toISOString(),
        level: 'error' as const,
        component: 'Payment',
        message: 'Payment processing failed',
        details: 'Stripe API timeout'
      },
      {
        id: '4',
        timestamp: new Date(Date.now() - 30 * 60 * 1000).toISOString(),
        level: 'info' as const,
        component: 'User',
        message: 'New user registration',
        details: 'User ID: user-1234'
      },
      {
        id: '5',
        timestamp: new Date(Date.now() - 45 * 60 * 1000).toISOString(),
        level: 'critical' as const,
        component: 'Security',
        message: 'Multiple failed login attempts',
        details: 'IP: 192.168.1.100'
      }
    ]
  },

  updateSystemConfig: async (configId: string, value: string) => {
    await mockDelay(800)
    return { success: true, data: { configId, value } }
  },

  getSystemLogs: async (level?: string, page = 1, limit = 50) => {
    await mockDelay(600)
    
    const mockLogs: AdminSystemLog[] = Array.from({ length: 100 }, (_, i) => ({
      id: `log-${i + 1}`,
      level: ['info', 'warning', 'error', 'critical'][Math.floor(Math.random() * 4)] as any,
      message: `System log message ${i + 1}`,
      timestamp: new Date(Date.now() - Math.random() * 24 * 60 * 60 * 1000).toISOString(),
      userId: Math.random() > 0.5 ? `user-${Math.floor(Math.random() * 10) + 1}` : undefined,
      chatbotId: Math.random() > 0.7 ? `chatbot-${Math.floor(Math.random() * 5) + 1}` : undefined,
      metadata: Math.random() > 0.8 ? { ip: '192.168.1.1', userAgent: 'Mozilla/5.0' } : undefined
    }))

    return {
      success: true,
      data: {
        logs: mockLogs.slice((page - 1) * limit, page * limit),
        total: mockLogs.length,
        page,
        limit,
        totalPages: Math.ceil(mockLogs.length / limit)
      }
    }
  },

  getAuditLogs: async (page = 1, limit = 50) => {
    await mockDelay(600)
    
    const mockAuditLogs: AdminAuditLog[] = Array.from({ length: 50 }, (_, i) => ({
      id: `audit-${i + 1}`,
      action: ['user_suspended', 'chatbot_created', 'payment_processed', 'settings_updated'][Math.floor(Math.random() * 4)],
      adminId: 'admin-1',
      adminName: 'Admin User',
      targetType: ['user', 'chatbot', 'system'][Math.floor(Math.random() * 3)] as any,
      targetId: `target-${Math.floor(Math.random() * 100) + 1}`,
      changes: Math.random() > 0.5 ? { status: 'suspended', reason: 'Violation' } : undefined,
      timestamp: new Date(Date.now() - Math.random() * 7 * 24 * 60 * 60 * 1000).toISOString(),
      ipAddress: `192.168.1.${Math.floor(Math.random() * 255) + 1}`
    }))

    return {
      success: true,
      data: {
        logs: mockAuditLogs.slice((page - 1) * limit, page * limit),
        total: mockAuditLogs.length,
        page,
        limit,
        totalPages: Math.ceil(mockAuditLogs.length / limit)
      }
    }
  },

  getNotifications: async () => {
    await mockDelay(400)
    
    const mockNotifications: AdminNotification[] = [
      {
        id: '1',
        type: 'system',
        title: 'System Maintenance',
        message: 'Scheduled maintenance in 2 hours',
        priority: 'medium',
        isRead: false,
        createdAt: new Date().toISOString(),
        actionRequired: true,
        actionUrl: '/admin/system'
      },
      {
        id: '2',
        type: 'financial',
        title: 'High Revenue Day',
        message: 'Revenue exceeded $10,000 today',
        priority: 'high',
        isRead: false,
        createdAt: new Date().toISOString(),
        actionRequired: false
      },
      {
        id: '3',
        type: 'security',
        title: 'Suspicious Activity',
        message: 'Multiple failed login attempts detected',
        priority: 'critical',
        isRead: true,
        createdAt: new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString(),
        actionRequired: true,
        actionUrl: '/admin/security'
      }
    ]

    return { success: true, data: mockNotifications }
  },

  markNotificationRead: async (notificationId: string) => {
    await mockDelay(300)
    return { success: true, data: { notificationId, isRead: true } }
  },

  getSystemSettings: async () => {
    await mockDelay(500)
    return {
      success: true,
      data: {
        platformName: 'Chatfolio',
        commissionRate: 0.15,
        maxFileSize: 10 * 1024 * 1024, // 10MB
        allowedFileTypes: ['pdf', 'doc', 'docx', 'txt', 'jpg', 'png'],
        maintenanceMode: false,
        registrationEnabled: true,
        emailVerificationRequired: true,
        maxChatbotsPerUser: 10,
        maxMessagesPerDay: 1000
      }
    }
  },

  updateSystemSettings: async (settings: Partial<AdminSystemSettings>) => {
    await mockDelay(1000)
    return { success: true, data: settings }
  }
}

// Export Management
export const adminExportApi = {
  exportUsers: async (format: 'csv' | 'json' | 'xlsx', filters?: AdminFilters) => {
    await mockDelay(2000)
    return {
      success: true,
      data: {
        downloadUrl: `/api/admin/export/users.${format}`,
        filename: `users-export-${new Date().toISOString().split('T')[0]}.${format}`,
        recordCount: 1247
      }
    }
  },

  exportChatbots: async (format: 'csv' | 'json' | 'xlsx', filters?: AdminFilters) => {
    await mockDelay(2000)
    return {
      success: true,
      data: {
        downloadUrl: `/api/admin/export/chatbots.${format}`,
        filename: `chatbots-export-${new Date().toISOString().split('T')[0]}.${format}`,
        recordCount: 89
      }
    }
  },

  exportAnalytics: async (format: 'csv' | 'json' | 'xlsx', dateRange?: { start: string; end: string }) => {
    await mockDelay(3000)
    return {
      success: true,
      data: {
        downloadUrl: `/api/admin/export/analytics.${format}`,
        filename: `analytics-export-${new Date().toISOString().split('T')[0]}.${format}`,
        recordCount: 30
      }
    }
  }
}

// Logs Management
export const adminLogsApi = {
  getLogs: async (page = 1, limit = 20) => {
    await mockDelay(800)
    
    const mockLogs = Array.from({ length: 100 }, (_, i) => ({
      id: `log-${i + 1}`,
      timestamp: new Date(Date.now() - Math.random() * 7 * 24 * 60 * 60 * 1000).toISOString(),
      level: ['info', 'warning', 'error', 'critical', 'debug'][Math.floor(Math.random() * 5)] as any,
      category: ['system', 'user', 'security', 'payment', 'chatbot', 'api'][Math.floor(Math.random() * 6)] as any,
      component: ['System', 'Database', 'Payment', 'User', 'Security', 'API'][Math.floor(Math.random() * 6)],
      userId: Math.random() > 0.5 ? `user-${Math.floor(Math.random() * 10) + 1}` : undefined,
      userEmail: Math.random() > 0.5 ? `user${Math.floor(Math.random() * 10) + 1}@example.com` : undefined,
      message: `Log message ${i + 1} - ${['User login', 'Payment processed', 'System error', 'Database query', 'API call'][Math.floor(Math.random() * 5)]}`,
      details: Math.random() > 0.7 ? `Additional details for log ${i + 1}` : undefined,
      ipAddress: Math.random() > 0.6 ? `192.168.1.${Math.floor(Math.random() * 255) + 1}` : undefined,
      userAgent: Math.random() > 0.8 ? 'Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36' : undefined,
      sessionId: Math.random() > 0.9 ? `session-${Math.random().toString(36).substr(2, 9)}` : undefined
    }))

    const stats = {
      total: mockLogs.length,
      errors: mockLogs.filter(log => log.level === 'error').length,
      warnings: mockLogs.filter(log => log.level === 'warning').length,
      critical: mockLogs.filter(log => log.level === 'critical').length
    }

    return {
      logs: mockLogs.slice((page - 1) * limit, page * limit),
      total: mockLogs.length,
      stats
    }
  },

  exportLogs: async (filters: any) => {
    await mockDelay(2000)
    return {
      success: true,
      data: {
        downloadUrl: `/api/admin/logs/export.csv`,
        filename: `logs-export-${new Date().toISOString().split('T')[0]}.csv`,
        recordCount: 100
      }
    }
  },

  deleteLogs: async () => {
    await mockDelay(1500)
    return { success: true, data: { deletedCount: 100 } }
  }
} 